{ abs } := Math

type { IndexedNote } from ./note-index.civet

// Judgment windows, in seconds of real time against the note's startTime.
// A played note pairs with the nearest unhit chart note of the same pitch
// within WINDOW; a chart note more than WINDOW past due with no hit is a miss.
export PERFECT_WINDOW := 0.06
export GOOD_WINDOW := 0.15
export HIT_WINDOW := 0.25

export type Judgment = "perfect" | "good" | "early" | "late" | "wrong" | "miss"

export type JudgedHit =
  judgment: Judgment
  // seconds the input was off by; positive is late. Absent for "wrong".
  delta?: number
  note?: IndexedNote

type SessionNote =
  note: IndexedNote
  hit: boolean
  missed: boolean

// One play-through of one part: judges incoming noteOns against the chart and
// surfaces misses as the clock passes them. Pure — the caller supplies song
// times, so it is testable without audio or MIDI. Time must move forward;
// on a seek or loop the caller starts a fresh session.
export class PracticeSession
  notes: SessionNote[]
  startTime: number
  // Playback rate the times come in at. The clock hands us song seconds, which
  // advance at `rate` per real second, so the real-time windows above scale by
  // `rate` in song time — at half speed a hit must land twice as close on the
  // chart to be worth the same.
  rate: number
  perfectWindow: number
  goodWindow: number
  hitWindow: number
  missCursor = 0
  stats = { perfect: 0, good: 0, early: 0, late: 0, wrong: 0, miss: 0 }
  // Wait-mode gates: the chart grouped by start time — a chord is one gate.
  // The transport freezes the clock at the next blocking gate's time (see
  // nextGateTime) until every note in it resolves.
  gates: {time: number, entries: SessionNote[]}[] = []
  gateCursor = 0

  // `notes` is the chart: already filtered to the part being played, sorted by
  // startTime. Notes before `startTime` are not part of the session.
  @(notes: IndexedNote[], startTime = 0, rate = 1)
    @startTime = startTime
    @rate = rate
    @perfectWindow = PERFECT_WINDOW * rate
    @goodWindow = GOOD_WINDOW * rate
    @hitWindow = HIT_WINDOW * rate
    @notes = notes.map (note) -> {note, hit: false, missed: false}
    while @missCursor < @notes.length and @notes[@missCursor].note.startTime < startTime - @hitWindow
      @notes[@missCursor].hit = true // exempt, not scored
      @missCursor++

    // notes are sorted, so equal start times are adjacent. Notes sharing a
    // startBeat get the identical computed startTime, so exact equality is
    // the chord test.
    let lastGate: {time: number, entries: SessionNote[]}?
    @notes.forEach (entry) =>
      {startTime: t} := entry.note
      if lastGate and t is lastGate.time
        lastGate.entries.push entry
      else
        @gates.push lastGate = {time: t, entries: [entry]}

  // Song time of the first gate that still blocks — one with a note neither
  // hit nor missed — or undefined when nothing blocks. The cursor only moves
  // forward: resolved gates stay resolved.
  nextGateTime(): number?
    while @gateCursor < @gates.length
      gate := @gates[@gateCursor]
      return gate.time unless gate.entries.every (e) -> e.hit or e.missed
      @gateCursor++
    undefined

  // Judge a played noteOn of pitch `n` at song time `t`.
  judgeNoteOn(n: number, t: number): JudgedHit
    best: SessionNote? .= undefined
    bestDelta .= Infinity

    for entry of @notes
      {note, hit} := entry
      break if note.startTime > t + @hitWindow
      continue if hit or note.n !== n
      delta := t - note.startTime
      continue if abs(delta) > @hitWindow
      if abs(delta) < abs(bestDelta)
        best = entry
        bestDelta = delta

    unless best
      @stats.wrong++
      return judgment: "wrong"

    best.hit = true
    judgment: Judgment :=
      if abs(bestDelta) <= @perfectWindow then "perfect"
      else if abs(bestDelta) <= @goodWindow then "good"
      else if bestDelta < 0 then "early"
      else "late"

    @stats[judgment]++

    // delta reported in real seconds, like the windows
    {judgment, delta: bestDelta / @rate, note: best.note}

  // End-of-pass sweep: every note starting before `endTime` that was neither
  // hit nor missed becomes a miss. The A/B wrap resets the clock before it
  // ever reaches the loop's end, so advanceTo alone leaves the final
  // hit-window's notes unresolved — a pass with its last note unplayed would
  // otherwise count as clean. Strict `<`: a note exactly at the loop's B
  // belongs to the next section.
  finishThrough(endTime: number): void
    for entry of @notes
      break if entry.note.startTime >= endTime
      if !entry.hit and !entry.missed
        entry.missed = true
        @stats.miss++
    return

  // Advance the clock to song time `t`, returning chart notes that are now
  // past saving. Monotonic — a backwards `t` returns nothing.
  advanceTo(t: number): IndexedNote[]
    missed: IndexedNote[] := []

    while @missCursor < @notes.length
      entry := @notes[@missCursor]
      break if entry.note.startTime > t - @hitWindow
      if !entry.hit and !entry.missed
        entry.missed = true
        @stats.miss++
        missed.push entry.note
      @missCursor++

    missed

export default PracticeSession
